import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSchedules, createSchedule, deleteSchedule, getUsers, getBranches, getForecasting } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Plus, Trash2, Clock, TrendingUp, User } from 'lucide-react';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';

export const SchedulePage = () => {
  const { hasRole } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const [formData, setFormData] = useState({
    user_id: '',
    branch_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const startDate = format(weekStart, 'yyyy-MM-dd');
      const endDate = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      
      const promises = [
        getSchedules({ start_date: startDate, end_date: endDate })
      ];

      if (hasRole(['admin', 'manager', 'supervisor'])) {
        promises.push(getUsers(), getBranches());
        if (hasRole(['admin', 'manager'])) {
          promises.push(getForecasting(2));
        }
      }

      const results = await Promise.all(promises);
      setSchedules(results[0].data);
      
      if (results[1]) setUsers(results[1].data);
      if (results[2]) setBranches(results[2].data);
      if (results[3]) setForecast(results[3].data.slice(0, 7));
    } catch (error) {
      console.error('Failed to fetch schedule data:', error);
    } finally {
      setLoading(false);
    }
  }, [weekStart, hasRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateShift = async (e) => {
    e.preventDefault();
    try {
      await createSchedule(formData);
      toast.success('Shift created successfully');
      setDialogOpen(false);
      setFormData({
        user_id: '',
        branch_id: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleDeleteShift = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shift?')) return;
    try {
      await deleteSchedule(id);
      toast.success('Shift deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shift');
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setFormData(prev => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
  };

  const handlePrevWeek = () => {
    setWeekStart(prev => addDays(prev, -7));
  };

  const handleNextWeek = () => {
    setWeekStart(prev => addDays(prev, 7));
  };

  const getSchedulesForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return schedules.filter(s => s.date === dateStr);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="schedule-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Schedule
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage work schedules
          </p>
        </div>
        
        {hasRole(['admin', 'manager', 'supervisor']) && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-shift-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Shift
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Manrope']">Create New Shift</DialogTitle>
                <DialogDescription>
                  Assign a shift to an employee
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateShift} className="space-y-4">
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <Select
                    value={formData.user_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, user_id: value }))}
                  >
                    <SelectTrigger data-testid="shift-user-select">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, branch_id: value }))}
                  >
                    <SelectTrigger data-testid="shift-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    className="rounded-md border"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                      data-testid="shift-start-time"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                      data-testid="shift-end-time"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Any additional notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    data-testid="shift-notes"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="submit-shift-btn">
                  Create Shift
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Forecasting - For managers */}
      {hasRole(['admin', 'manager']) && forecast.length > 0 && (
        <Card data-testid="schedule-forecast-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base font-['Manrope']">
              <TrendingUp className="w-4 h-4" />
              Recommended Staffing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {forecast.map((day, index) => (
                <div 
                  key={index}
                  className="text-center p-2 rounded-lg bg-muted/50"
                >
                  <p className="text-xs text-muted-foreground">{day.day_name.slice(0, 3)}</p>
                  <p className="text-lg font-bold font-['JetBrains_Mono']">{day.recommended_staff}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={handlePrevWeek} data-testid="prev-week-btn">
          Previous Week
        </Button>
        <h2 className="text-lg font-semibold font-['Manrope']">
          {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
        </h2>
        <Button variant="outline" onClick={handleNextWeek} data-testid="next-week-btn">
          Next Week
        </Button>
      </div>

      {/* Schedule Grid */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
        {weekDays.map((day) => {
          const daySchedules = getSchedulesForDate(day);
          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
          
          return (
            <Card 
              key={day.toISOString()} 
              className={`${isToday ? 'ring-2 ring-primary' : ''}`}
              data-testid={`schedule-day-${format(day, 'yyyy-MM-dd')}`}
            >
              <CardHeader className="p-4 pb-2">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">
                    {format(day, 'EEE')}
                  </p>
                  <p className={`text-2xl font-bold font-['Manrope'] ${isToday ? 'text-primary' : ''}`}>
                    {format(day, 'd')}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0 space-y-2">
                {daySchedules.length === 0 ? (
                  <p className="text-xs text-center text-muted-foreground py-4">
                    No shifts
                  </p>
                ) : (
                  daySchedules.map((schedule) => (
                    <div
                      key={schedule.id}
                      className="p-2 rounded-lg bg-muted/50 text-sm group"
                      data-testid={`shift-${schedule.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span className="font-medium truncate">
                            {schedule.user_name || 'Employee'}
                          </span>
                        </div>
                        {hasRole(['admin', 'manager', 'supervisor']) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={() => handleDeleteShift(schedule.id)}
                            data-testid={`delete-shift-${schedule.id}`}
                          >
                            <Trash2 className="w-3 h-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Clock className="w-3 h-3" />
                        {schedule.start_time} - {schedule.end_time}
                      </div>
                      {schedule.branch_name && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          {schedule.branch_name}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
